import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./TaskPage.css";
import search from "../../../assets/search.png";

type Answer = {
  answerId: string | number;
  text: string;
};

type Question = {
  questionId: string | number;
  number?: number;
  text: string;
  type: "checkbox" | "radio";
  requiredAnswersCount?: number;
  answers: Answer[];
  result?: string;
};

type TaskSection = {
  id?: string;
  field?: string;
  title: string;
  text: string;
  sourceQuestionNumber?: number;
  isRevealed?: boolean;
};

type Task = {
  id: string | number;
  multi_case_id?: string;
  number?: number;
  order?: number;
  diagnosis?: string;
  description: TaskSection[];
  questions: Question[];
};

type SelectedAnswers = string[][][];
type RevealedSections = Record<number, TaskSection[]>;

type StoredAttempt = {
  taskAttemptId: string;
  tasks: Task[];
};

type RouteState = {
  taskAttemptId?: string;
  taskIndex?: number;
};

const TASK_ATTEMPT_STORAGE_KEY = "taskAttemptData";
const REVEALED_SECTIONS_STORAGE_KEY = "taskRevealedSections";

const createEmptyAnswers = (tasks: Task[]): SelectedAnswers =>
  tasks.map((task) => task.questions.map(() => []));

const getSectionId = (section: TaskSection, index: number) =>
  section.id || section.field || `${section.title}-${index}`;

const stripImageMarkers = (text: string) =>
  text.replace(/<<<image:[^>]+>>>/g, "").trim();

const formatStudentDisplayName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[1]} ${parts[0]}`;
  }

  return fullName.trim();
};

const parseResultSections = (result: string, questionNumber: number): TaskSection[] => {
  const cleanResult = String(result || "").trim();

  if (!cleanResult) return [];

  const lines = cleanResult
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const title = stripImageMarkers(lines[0] || "") || "Новая информация";

  return [
    {
      id: `revealed-q-${questionNumber}`,
      title,
      text: cleanResult,
      sourceQuestionNumber: questionNumber,
      isRevealed: true,
    },
  ];
};

const renderTextWithImages = (text: string) => {
  const parts = String(text || "").split(/(<<<image:[^>]+>>>)/g);

  return parts.map((part, index) => {
    const imageMatch = part.match(/^<<<image:([^>]+)>>>$/);

    if (imageMatch) {
      return (
        <div key={`${part}-${index}`} className="task-image-placeholder">
          Изображение: {imageMatch[1]}
        </div>
      );
    }

    return part
      .split(/\r?\n/)
      .filter((line) => line.trim())
      .map((line, lineIndex) => (
        <p key={`${index}-${lineIndex}`} className="task-section-line">
          {line}
        </p>
      ));
  });
};

const TaskPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as RouteState | null;
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskAttemptId, setTaskAttemptId] = useState("");
  const [currentSituationIndex, setCurrentSituationIndex] = useState(
    routeState?.taskIndex || 0
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswers>([]);
  const [revealedSections, setRevealedSections] = useState<RevealedSections>({});
  const [activeSectionId, setActiveSectionId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const savedTime = localStorage.getItem("timeLeft");
    return savedTime ? parseInt(savedTime, 10) : 30 * 60;
  });
  const studentFullName = localStorage.getItem("fullName") || "";
  const studentDisplayName = formatStudentDisplayName(studentFullName);

  const currentTask = tasks[currentSituationIndex];
  const currentQuestion = currentTask?.questions[currentQuestionIndex];
  const visibleSections = useMemo(
    () => [
      ...(currentTask?.description || []),
      ...(revealedSections[currentSituationIndex] || []),
    ],
    [currentTask, revealedSections, currentSituationIndex]
  );
  const selectedCurrentAnswers =
    selectedAnswers[currentSituationIndex]?.[currentQuestionIndex] || [];
  const requiredAnswersCount = currentQuestion?.requiredAnswersCount || 1;
  const isNextButtonVisible =
    selectedCurrentAnswers.length >= requiredAnswersCount && !isSubmitting;

  useEffect(() => {
    const loadTasks = async () => {
      const username = localStorage.getItem("username") || "";
      const specialty = localStorage.getItem("specialty") || "";
      const storedAttemptRaw = localStorage.getItem(TASK_ATTEMPT_STORAGE_KEY);
      const storedRevealedRaw = localStorage.getItem(REVEALED_SECTIONS_STORAGE_KEY);

      setIsLoading(true);
      setErrorMessage("");

      try {
        if (routeState?.taskAttemptId && storedAttemptRaw) {
          const storedAttempt = JSON.parse(storedAttemptRaw) as StoredAttempt;

          if (storedAttempt.taskAttemptId === routeState.taskAttemptId) {
            setTasks(storedAttempt.tasks);
            setTaskAttemptId(storedAttempt.taskAttemptId);
            setCurrentSituationIndex(routeState.taskIndex || 0);
            setSelectedAnswers(createEmptyAnswers(storedAttempt.tasks));

            if (storedRevealedRaw) {
              setRevealedSections(JSON.parse(storedRevealedRaw));
            }

            return;
          }
        }

        const params = new URLSearchParams({ username, specialty });
        const response = await fetch(
          `/api/task-questions?${params.toString()}`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Не удалось загрузить ситуационные задачи");
        }

        const loadedTasks: Task[] = Array.isArray(data.tasks) ? data.tasks : [];
        const loadedAttemptId = data.taskAttemptId || data.attemptId || "";

        setTasks(loadedTasks);
        setTaskAttemptId(loadedAttemptId);
        setCurrentSituationIndex(0);
        setSelectedAnswers(createEmptyAnswers(loadedTasks));
        setRevealedSections({});
        localStorage.setItem(
          TASK_ATTEMPT_STORAGE_KEY,
          JSON.stringify({ taskAttemptId: loadedAttemptId, tasks: loadedTasks })
        );
        localStorage.setItem("taskAttemptId", loadedAttemptId);
        localStorage.setItem("startTime", new Date().toISOString());
        localStorage.removeItem(REVEALED_SECTIONS_STORAGE_KEY);
        localStorage.removeItem("taskResult");
        localStorage.removeItem("taskResults");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить ситуационные задачи";
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadTasks();
  }, [routeState?.taskAttemptId, routeState?.taskIndex]);

  useEffect(() => {
    const firstSection = currentTask?.description?.[0];

    if (firstSection) {
      setActiveSectionId(getSectionId(firstSection, 0));
    }
  }, [currentSituationIndex, currentTask]);

  useEffect(() => {
    localStorage.setItem(
      REVEALED_SECTIONS_STORAGE_KEY,
      JSON.stringify(revealedSections)
    );
  }, [revealedSections]);

  const scrollToSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const submitCurrentTask = async () => {
    if (!taskAttemptId || isSubmitting || !currentTask) return;

    const username = localStorage.getItem("username") || "";
    const answers = currentTask.questions.map((question, questionIndex) => ({
      taskId: currentTask.id,
      taskIndex: currentSituationIndex,
      questionId: question.questionId,
      questionIndex,
      selectedAnswers:
        selectedAnswers[currentSituationIndex]?.[questionIndex]?.map(String) || [],
    }));
    const endTime = new Date().toISOString();

    localStorage.setItem("endTime", endTime);
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/check-task-result", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          taskAttemptId,
          taskIndex: currentSituationIndex,
          answers,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось проверить результат");
      }

      const savedResults = JSON.parse(localStorage.getItem("taskResults") || "[]");
      const nextResults = [
        ...savedResults.filter(
          (item: { taskIndex?: number }) => item.taskIndex !== data.taskIndex
        ),
        data,
      ];

      localStorage.setItem("taskResult", JSON.stringify(data));
      localStorage.setItem("taskResults", JSON.stringify(nextResults));
      localStorage.setItem("lastTaskAttemptId", data.attemptId);

      if (data.isFinalTask) {
        localStorage.removeItem("timeLeft");
      }

      navigate("/task-review", {
        state: {
          attemptId: data.attemptId,
          taskAttemptId: data.taskAttemptId,
          taskIndex: data.taskIndex,
          isFinalTask: data.isFinalTask,
          totalTasks: data.totalTasks,
        },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Не удалось проверить результат";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isLoading || errorMessage) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          submitCurrentTask();
          return 0;
        }

        localStorage.setItem("timeLeft", (prev - 1).toString());
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, errorMessage, taskAttemptId, selectedAnswers, currentSituationIndex]);

  const handleAnswerSelect = (answerId: string | number) => {
    if (!currentQuestion) return;

    const answerKey = String(answerId);
    const currentAnswers =
      selectedAnswers[currentSituationIndex]?.[currentQuestionIndex] || [];
    const isAlreadySelected = currentAnswers.includes(answerKey);

    setSelectedAnswers((prev) =>
      prev.map((taskAnswers, taskIndex) =>
        taskIndex === currentSituationIndex
          ? taskAnswers.map((questionAnswers, questionIndex) => {
              if (questionIndex !== currentQuestionIndex) return questionAnswers;

              if (currentQuestion.type === "checkbox") {
                const nextAnswers = isAlreadySelected
                  ? questionAnswers.filter((answer) => answer !== answerKey)
                  : [...questionAnswers, answerKey];

                return nextAnswers.slice(0, requiredAnswersCount);
              }

              return [answerKey];
            })
          : taskAnswers
      )
    );
  };

  const revealCurrentQuestionInfo = () => {
    if (!currentQuestion?.result) return;

    const questionNumber = currentQuestion.number || currentQuestionIndex + 1;
    const newSections = parseResultSections(currentQuestion.result, questionNumber);

    if (!newSections.length) return;

    const currentSections = revealedSections[currentSituationIndex] || [];
    const currentIds = new Set(
      currentSections.map((section, index) => getSectionId(section, index))
    );
    const uniqueSections = newSections.filter(
      (section, index) => !currentIds.has(getSectionId(section, index))
    );

    if (!uniqueSections.length) return;

    setRevealedSections((prev) => ({
      ...prev,
      [currentSituationIndex]: [
        ...(prev[currentSituationIndex] || []),
        ...uniqueSections,
      ],
    }));
    const newSectionId = getSectionId(uniqueSections[0], visibleSections.length);
    setActiveSectionId(newSectionId);
    window.setTimeout(() => scrollToSection(newSectionId), 0);
    window.alert("Появилась новая информация");
  };

  const handleNextQuestion = () => {
    if (!currentTask) return;

    revealCurrentQuestionInfo();

    if (currentQuestionIndex < currentTask.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      return;
    }

    submitCurrentTask();
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  if (isLoading) {
    return <div className="task-page">Загрузка ситуационных задач...</div>;
  }

  if (errorMessage) {
    return <div className="task-page">{errorMessage}</div>;
  }

  if (!currentTask || !currentQuestion) {
    return <div className="task-page">Нет доступных ситуационных задач.</div>;
  }

  return (
    <div className="task-page">
      <div className="title">
        <div className="timer">
          <span className="timer-label">Оставшееся время:</span>{" "}
          <span className="timer-value">0:{formatTime(timeLeft)}</span>
        </div>
        {studentFullName && (
          <div className="exam-user-menu">
            <button
              type="button"
              className="exam-user-button"
              onClick={() => setIsUserMenuOpen((isOpen) => !isOpen)}
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
            >
              <span className="exam-user-name">{studentDisplayName}</span>
              <span className="exam-user-avatar"></span>
              <span className="exam-user-caret" aria-hidden="true"></span>
            </button>
            {isUserMenuOpen && (
              <div className="exam-user-dropdown" role="menu">
                <button type="button" onClick={handleLogout} role="menuitem">
                  Выход
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="title22">
        <div>Решение ситуационных задач - 3-й этап (Мультикейсы)</div>
        <div className="imgCont">
          <img src={search} alt="" style={{ width: 20 }} />
        </div>
      </div>
      <div className="task-container">
        <div className="task-top">
          <aside className="static-text">
            <div className="static-title">условие ситуационной задачи</div>
            {visibleSections.map((section, index) => {
              const sectionId = getSectionId(section, index);

              return (
                <button
                  key={`${sectionId}-${index}`}
                  type="button"
                  className={`section-nav-item ${
                    activeSectionId === sectionId ? "active" : ""
                  } ${section.isRevealed ? "revealed" : ""}`}
                  onClick={() => scrollToSection(sectionId)}
                >
                  {section.title || `Раздел ${index + 1}`}
                </button>
              );
            })}
          </aside>

          <main className="task-situation">
            <h2>УСЛОВИЕ СИТУАЦИОННОЙ ЗАДАЧИ</h2>
            {visibleSections.length ? (
              visibleSections.map((section, index) => {
                const sectionId = getSectionId(section, index);

                return (
                  <section
                    key={`${sectionId}-content-${index}`}
                    id={sectionId}
                    ref={(element) => {
                      sectionRefs.current[sectionId] = element;
                    }}
                    className={`task-section ${
                      activeSectionId === sectionId ? "active" : ""
                    }`}
                  >
                    <h3>{section.title || `Раздел ${index + 1}`}</h3>
                    <div className="situation-scroll">
                      {renderTextWithImages(section.text)}
                    </div>
                  </section>
                );
              })
            ) : (
              <p>Нет данных для отображения.</p>
            )}
          </main>
        </div>

        <div className="task-bottom">
          <div className="task-progress">
            <h4>Вопрос {currentQuestionIndex + 1}</h4>
          </div>

          <div className="task-answers">
            <div className="required-answers">
              Необходимое количество ответов в данном вопросе: {requiredAnswersCount}
            </div>
            <div className="task-question">
              <h3>{currentQuestion.text}</h3>
            </div>
            <div className="answers">
              {currentQuestion.answers.map((answer, index) => (
                <label key={answer.answerId} className="answer-option">
                  <input
                    type={
                      currentQuestion.type === "checkbox"
                        ? "checkbox"
                        : "radio"
                    }
                    name={`question-${currentSituationIndex}-${currentQuestionIndex}`}
                    value={answer.answerId}
                    checked={selectedCurrentAnswers.includes(String(answer.answerId))}
                    onChange={() => handleAnswerSelect(answer.answerId)}
                  />
                  <span>
                    {String.fromCharCode(65 + index)}. {answer.text}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        {isNextButtonVisible && (
          <button onClick={handleNextQuestion} className="next-button">
            {isSubmitting
              ? "Проверка..."
              : currentQuestionIndex < currentTask.questions.length - 1
              ? "Следующая страница"
              : "Завершить ситуационную задачу"}
          </button>
        )}
      </div>
    </div>
  );
};

export default TaskPage;
